import { useState } from "react";
import { ChevronDown } from "lucide-react";

interface FAQItem {
  question: string;
  answer: string;
}

const FAQ_ITEMS: FAQItem[] = [
  {
    question: "Is Kin a therapist or doctor?",
    answer:
      "No. Kin is here to listen, support you, and help you think things through, but Kin is not a licensed therapist, counselor, doctor, or medical professional. If you ask about a medical concern for yourself or your child, Kin will always point you to your doctor or pediatrician. If you're going through something emotionally heavy, Kin will always encourage you to talk to a real person. And if you or someone you know is in crisis, please reach out to the 988 Suicide & Crisis Lifeline (call or text 988) anytime. Kin is a great friend in your corner, not a replacement for professional care.",
  },
  {
    question: "What happens with my data?",
    answer:
      "Your conversations, grocery lists, schedules, and everything else you share with Kin are stored securely and are yours. We deliberately moved Kin off of a third-party hosting platform and onto our own self-hosted backend infrastructure so that your data stays under our direct control, not someone else's. Kin does not sell your data, share it with advertisers, or use it to train AI models. Your conversations are processed using AI technology whose commercial terms also prohibit training on your data. Your information is used only to make Kin work for you. If you delete your account, everything, including information about your family, is permanently deleted immediately.",
  },
  {
    question: "Is it safe to use around my kids?",
    answer:
      "Yes. Kin never uses profanity, religious language as filler, or inappropriate content. It was built with families in mind. That said, Kin is designed for adults, not as a tool for kids to use directly. We don't knowingly collect personal information from children under 13.",
  },
  {
    question: "Is Kin designed for kids?",
    answer:
      "Kin is built for moms, not for kids to use directly. It references your children's names, schedules, and activities because that's your life, but Kin is an adult tool. Information about your children comes from you and is only used to personalize your experience. We never share your family's information with third parties and never use it to train AI models. If you delete your account, all of your data, including info about your children, is permanently deleted.",
  },
  {
    question: "Can my partner use Kin too?",
    answer:
      "Right now, Kin is just yours 💜 One mom, one account — your second brain, not another shared app to manage. When your crew needs to know things, Kin helps you send them: texts, plans, sitter sheets, the works. And yes, a family version of Kin is coming down the road. For now, she's all about you.",
  },
  {
    question: "So what exactly is Kin?",
    answer:
      "Kin is an AI assistant built specifically for moms. Not a generic chatbot with a pink skin. Kin understands meal planning, school schedules, work emails, bedtime meltdowns, garden zones, and everything in between because it was built by a mom who needed all of those things in one place. Think of it like a really smart friend who's always available, never judges, and actually gives you useful answers.",
  },
  {
    question: "How is Kin different from ChatGPT?",
    answer:
      "ChatGPT and other AI tools are incredible general-purpose assistants. Kin is purpose-built for your life as a mom. It connects to your Google Calendar, plans meals with real prices at your store, and talks to you like a friend, not a robot. You don't need to write perfect prompts or know anything about AI. Just talk to it like you'd text a friend. And the more you use Kin, the more you'll pick up about how AI works without even trying. Kin drops little tips along the way so you're learning without feeling like you're in a class.",
  },
  {
    question: "Does Kin give real advice?",
    answer:
      "Kin gives you real, actionable help for everyday life: meal plans, email drafts, schedules, packing lists, garden plans, meeting prep. For medical questions, Kin will always point you to your doctor or pediatrician. For legal or financial questions, Kin will suggest you talk to a professional. Kin is incredibly helpful and honest about what it can't do.",
  },
  {
    question: "What about AI and the environment?",
    answer:
      "Fair question. AI does use energy and water to run, just like streaming, cloud storage, and every other digital tool you use daily. Kin uses efficient AI models and only runs what's needed to give you a great experience. As we add more features, we'll keep making responsible choices as the technology and the science around its environmental impact continue to evolve.",
  },
  {
    question: "Who built Kin?",
    answer:
      "Kin was built by the mom behind Kinyoudoitall. She was drowning in the mental load of working full-time, raising two boys, and trying to hold onto the things that made her her. She went looking for the perfect AI tool to recommend to other moms, couldn't find one, and built it herself with zero tech background. Follow the journey at @kinyoudoitall on Instagram, TikTok, Facebook, and Pinterest, or read the blog at kinyoudoitall.com.",
  },
];

export { FAQ_ITEMS };

interface KinFAQProps {
  embedded?: boolean;
}

export default function KinFAQ({ embedded = false }: KinFAQProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const list = (
    <div
      style={
        embedded
          ? undefined
          : {
              background: "white",
              borderRadius: 16,
              border: "1px solid #E0E0F0",
              overflow: "hidden",
            }
      }
    >
        {FAQ_ITEMS.map((item, index) => (
          <div key={index}>
            {index > 0 && (
              <div style={{ height: 1, background: "#E0E0F0", margin: "0 16px" }} />
            )}
            <button
              onClick={() => toggle(index)}
              aria-expanded={openIndex === index}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 16px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                textAlign: "left" as const,
                gap: 12,
              }}
            >
              <span
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: "#1C1A3E",
                  lineHeight: 1.4,
                  flex: 1,
                }}
              >
                {item.question}
              </span>
              <ChevronDown
                size={18}
                color="#5050C8"
                style={{
                  flexShrink: 0,
                  transition: "transform 200ms ease",
                  transform: openIndex === index ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </button>
            <div
              style={{
                maxHeight: openIndex === index ? 400 : 0,
                overflow: "hidden",
                transition: "max-height 200ms ease",
              }}
            >
              <div
                style={{
                  padding: "0 16px 16px 16px",
                  fontSize: 14,
                  lineHeight: 1.65,
                  color: "#54525C",
                }}
              >
                {item.answer}
              </div>
            </div>
          </div>
        ))}
    </div>
  );

  if (embedded) return list;

  return (
    <div style={{ marginBottom: 32 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 1.5,
          textTransform: "uppercase" as const,
          color: "#54525C",
          marginBottom: 12,
        }}
      >
        Questions About Kin
      </div>
      {list}
    </div>
  );
}
